import { Ref, nextTick, reactive, computed, unref, watchEffect, MaybeRefOrGetter } from 'vue';
import {
  useElementBounding,
  useEventListener,
  MaybeElement,
} from '@vueuse/core';
import { TooltipPosition } from '@ui-components/UITooltip';

// Tooltip options interface
export interface TooltipOptions {
  ref: Ref<MaybeElement> | MaybeElement; // Keep the ref type
  content: string | Ref<string>;
  left?: number; 
  top?: number; 
  elementWidth?: number; 
  elementHeight?: number; 
  isOutside?: boolean; 
  position?: TooltipPosition;
}

export interface Tooltip {
  id: string;
  options: TooltipOptions; // Keep the tooltip options type
}

interface State {
  tooltips: Map<string, Tooltip>; // Store tooltips in a map
}

const state = reactive<State>({
  tooltips: new Map(),
});

export const useTooltip = (options: TooltipOptions) => {
  const id = randomString(16);

  // Cry everytime i see this, needed to make sure element is loaded
  watchEffect(() => {
    const targetElement = unref(options.ref) as MaybeElement | null;

    if (!targetElement) {
      return;
    }

    // Use the mouseenter event to set the tooltip and calculate positions
    useEventListener(targetElement as MaybeRefOrGetter<EventTarget>, 'mouseenter', async () => {
      await nextTick(); // Wait for DOM updates

      // Get element bounding values
      const { left, top, width: elementWidth, height: elementHeight } = useElementBounding(targetElement);
      
      const tooltip = {
        id,
        options: {
          ref: targetElement,
          content: unref(options.content),
          left: unref(left) + (options?.left ?? 0),
          top: unref(top) + (options?.top ?? 0),
          elementWidth: unref(elementWidth),
          elementHeight: unref(elementHeight),
          isOutside: false,
        },
      };

      state.tooltips.set(id, tooltip); 
    });

    useEventListener(targetElement as MaybeRefOrGetter<EventTarget>, 'mouseleave', () => {
      state.tooltips.delete(id);
    });

    useEventListener(targetElement as MaybeRefOrGetter<EventTarget>, 'click', () => {
      state.tooltips.delete(id);
    });
  });
}

export const useTooltips = () => computed(() => state.tooltips);
